import Head from 'next/head';
import type { PropsWithChildren } from 'react';
import { toLocalizedUrl } from 'tradera/utils/url';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import openGraphTraderaImage from 'static/images/logos/open-graph-tradera-default.png';
import { LANGUAGE_LOCALE } from 'tradera/lang/constants.mjs';

export type PageHeadProps = PropsWithChildren & {
    ldJsons?: string[];
    canonicalUrl: string;
    isFollow?: boolean;
    isNoIndex?: boolean;
    isNoFollow?: boolean;
    locale: string;
    metaDescription: string;
    ogDescription: string;
    ogImage?: string;
    ogTitle: string;
    ogType?: string;
    pageTitle: string;
};

export const PageHead = ({
    ldJsons,
    children,
    canonicalUrl,
    isFollow,
    isNoIndex,
    isNoFollow,
    pageTitle,
    locale,
    metaDescription,
    ogTitle,
    ogType,
    ogDescription,
    ogImage
}: PageHeadProps) => {
    const isAlternateHrefLangLinksEnabled = useIsFeatureEnabled(
        'alternate-href-lang-links'
    );
    const ogImageHttps = ogImage || openGraphTraderaImage;
    const ogImageHttp = ogImageHttps.replace(/^https:/, 'http:');

    const robotsContent = [
        isNoIndex && 'noindex',
        isFollow && 'follow',
        isNoFollow && 'nofollow'
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <Head>
            <title>{pageTitle}</title>
            {robotsContent.length > 0 && (
                <meta name="robots" content={robotsContent} />
            )}
            <link
                rel="canonical"
                href={toLocalizedUrl(canonicalUrl, locale)}
                key="canonical"
            />
            {isAlternateHrefLangLinksEnabled && (
                <>
                    {Object.entries(LANGUAGE_LOCALE).map(
                        ([languageCodeIso2, locale]) => (
                            <link
                                key={locale}
                                rel="alternate"
                                href={toLocalizedUrl(
                                    canonicalUrl,
                                    languageCodeIso2
                                )}
                                hrefLang={locale}
                            />
                        )
                    )}
                    <link
                        rel="alternate"
                        href={toLocalizedUrl(canonicalUrl, 'en')}
                        hrefLang="x-default"
                    />
                </>
            )}
            <meta
                name="description"
                content={metaDescription}
                key="description"
            />
            <meta
                property="og:url"
                content={toLocalizedUrl(canonicalUrl, locale)}
                key="og:url"
            />
            <meta property="og:title" content={ogTitle} key="og:title" />
            <meta
                property="og:description"
                content={ogDescription}
                key="og:description"
            />
            <meta property="og:image" content={ogImageHttp} key="og:image" />
            {ogType && <meta property="og:type" content={ogType} />}
            <meta
                property="og:image:secure_url"
                content={ogImageHttps}
                key="og:image:secure_url"
            />
            {ldJsons &&
                ldJsons.map((ldJson) => (
                    <script
                        key={ldJson}
                        type="application/ld+json"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: ldJson
                        }}
                    />
                ))}
            {children}
        </Head>
    );
};
